import React, { useMemo, useState } from 'react';
import { Checkbox, Radio, Select } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { REPORT_CONFIG } from '../../constants';
import type { RadUser } from '../../types';

// Alphabetical, case-insensitive ordering for the user dropdowns.
const byLabel = (a: { label: string }, b: { label: string }) =>
  (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' });

// Type-to-filter on the visible name; the username is matched too so users can
// search either way.
const userSelectProps = {
  showSearch: true,
  filterOption: (input: string, option?: any) => {
    const needle = input.trim().toLowerCase();
    if (!needle) return true;
    return (
      String(option?.label ?? '').toLowerCase().includes(needle) ||
      String(option?.value ?? '').toLowerCase().includes(needle)
    );
  },
} as const;

interface AdvancedOptionsProps {
  radUsers: RadUser[];
  allUsers: any[];
  /** Disable the signature-driving controls (co-signer + proxy) while a signature preview
   *  is rebuilding + reopening the document, so a second preview can't fire mid-rebuild. */
  locked?: boolean;
  onNotifyPhysician: () => void;
  onCosignChange: (val: string, userData: RadUser) => void;
  isSignedOff: boolean;
  currentUsername: string | undefined;
  onProxyDraftChange: (val: string | null) => void;
  onProxySignoffChange: (val: string | null) => void;
  correlated?: string | null;
  diagnosed?: string | null;
  onCorrelatedChange?: (val: string) => void;
  onDiagnosedChange?: (val: string) => void;
}

const AdvancedOptions: React.FC<AdvancedOptionsProps> = ({
  radUsers,
  allUsers,
  locked = false,
  onNotifyPhysician,
  onCosignChange,
  isSignedOff,
  currentUsername,
  onProxyDraftChange,
  onProxySignoffChange,
  correlated,
  diagnosed,
  onCorrelatedChange,
  onDiagnosedChange,
}) => {
  const [activeAction, setActiveAction] = useState<string | null>(null);

  const radUserOptions = useMemo(() =>
    radUsers
      .map(u => ({ label: u.user_fullname || u.username, value: u.username, data: u }))
      .sort((a, b) => byLabel(a, b)),
  [radUsers]);

  const filteredRadOptions = useMemo(() =>
    radUserOptions.filter(u => u.value !== currentUsername),
  [radUserOptions, currentUsername]);

  // Signature-bearing roles (co-signer, proxy signer) are restricted to users who actually
  // have a signature on file. Picking one who doesn't stamps an empty block — the doctor
  // sees "co-signed by X" in the preview and the signed PDF carries nothing for X. They are
  // shown DISABLED rather than hidden, so it's clear why a colleague can't be chosen.
  const signerRadOptions = useMemo(() =>
    filteredRadOptions.map(o => (
      o.data?.has_signature === false
        ? { ...o, label: `${o.label} (no signature on file)`, disabled: true }
        : o
    )),
  [filteredRadOptions]);

  const allUserOptions = useMemo(() =>
    allUsers
      .map(u => ({ label: u.user_fullname || u.username, value: u.username }))
      .sort((a, b) => byLabel(a, b)),
  [allUsers]);

  return (
    <div className="collapsible-content">
      {REPORT_CONFIG.SHOW_NOTIFY_PHYSICIAN && (
        <div className="advanced-option-item">
          <a className="notify-physician-link" onClick={onNotifyPhysician}>
            <BellOutlined className="notify-bell-icon" />
            Notify Physician
          </a>
        </div>
      )}

      <div className="advanced-option-item">
        <Checkbox
          disabled={locked}
          onChange={e => {
            setActiveAction(e.target.checked ? 'co_signing' : null);
            // Unchecking clears the co-signer so the signature preview drops that block.
            if (!e.target.checked) onCosignChange(null as any, undefined as any);
          }}
          className="advanced-option-checkbox"
        >
          Report Co-Signing
        </Checkbox>
        {activeAction === 'co_signing' && (
          <Select
            allowClear
            disabled={locked}
            {...userSelectProps}
            style={{ width: '100%', marginTop: '0.5rem' }}
            onChange={(val, opt: any) => onCosignChange(val ?? null, opt?.data)}
            options={signerRadOptions}
            placeholder="Select radiologist for co-signing"
          />
        )}
      </div>

      {REPORT_CONFIG.SHOW_PROXY_OPTIONS && (
        <>
          <div className="advanced-option-item">
            <Checkbox
              disabled={isSignedOff || locked}
              onChange={e => {
                if (e.target.checked) {
                  setActiveAction('proxy-draft');
                  onProxyDraftChange(null);
                } else {
                  setActiveAction(null);
                  onProxyDraftChange(null);
                }
              }}
              className="advanced-option-checkbox"
            >
              Proxy Draft
            </Checkbox>
            {activeAction === 'proxy-draft' && (
              <>
                <Select
                  disabled={locked}
                  {...userSelectProps}
                  style={{ width: '100%', marginTop: '0.5rem' }}
                  onChange={val => onProxyDraftChange(val)}
                  options={filteredRadOptions}
                  placeholder="Select radiologist for proxy draft"
                />
                <div className="advanced-option-hint">
                  The draft is credited to the selected radiologist. No signature is added.
                </div>
              </>
            )}
          </div>

          <div className="advanced-option-item">
            <Checkbox
              disabled={locked}
              onChange={e => {
                if (e.target.checked) {
                  setActiveAction('proxy-signoff');
                  onProxySignoffChange(null);
                } else {
                  setActiveAction(null);
                  onProxySignoffChange(null);
                }
              }}
              className="advanced-option-checkbox"
            >
              Proxy Signoff
            </Checkbox>
            {activeAction === 'proxy-signoff' && (
              <Select
                disabled={locked}
                {...userSelectProps}
                style={{ width: '100%', marginTop: '0.5rem' }}
                onChange={val => onProxySignoffChange(val)}
                options={signerRadOptions}
                placeholder="Select radiologist for proxy signoff"
              />
            )}
          </div>
        </>
      )}

      {REPORT_CONFIG.SHOW_CORRELATED_DIAGNOSED && (
        <>
          <div className="advanced-option-item advanced-option-radio-group">
            <div className="radio-label">Clinically diagnosed</div>
            <Radio.Group value={diagnosed} onChange={e => onDiagnosedChange?.(e.target.value)}>
              <Radio value="diagnosed">Yes</Radio>
              <Radio value="notdiagnosed">No</Radio>
            </Radio.Group>
          </div>
          <div className="advanced-option-item advanced-option-radio-group">
            <div className="radio-label">Clinically correlated</div>
            <Radio.Group value={correlated} onChange={e => onCorrelatedChange?.(e.target.value)}>
              <Radio value="correlated">Yes</Radio>
              <Radio value="notcorrelated">No</Radio>
            </Radio.Group>
          </div>
        </>
      )}
    </div>
  );
};

export default AdvancedOptions;
